import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';

// Verizon logo
import opplaneLogo from '../../assets/opplane-logo.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { login, currentUser, error } = useAuth();
  const navigate = useNavigate();
  
  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      if (currentUser.role === 'admin' || currentUser.groups?.includes('admin')) {
        navigate('/admin');
      } else {
        navigate('/client');
      }
    }
  }, [currentUser, navigate]);
  
  // Update error message from auth context
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setErrorMessage('Please enter both email and password');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Don't clear error message here
      // This allows errors from the auth context to be displayed
      
      const result = await login(email, password);
      
      // Check if we need to change password
      if (result && result.passwordChangeRequired) {
        // Redirect to password change page
        navigate('/password-change');
        return;
      }
      
      // Only clear error if login was successful
      if (result) {
        setErrorMessage('');
      }
      
      // Navigation will happen in the useEffect above
    } catch (err) {
      console.error('Login error:', err);
      setErrorMessage(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <LoginContainer>
      <LoginCard>
        <LogoContainer>
          <OpplaneLogo src={opplaneLogo} alt="Opplane Logo" />
        </LogoContainer>
        
        <LoginForm onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errorMessage) setErrorMessage('');
              }}
              placeholder="Enter your email"
              disabled={isSubmitting}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              type="password"
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errorMessage) setErrorMessage('');
              }}
              placeholder="Enter your password"
              disabled={isSubmitting}
              required
            />
          </FormGroup>
          
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

          <LoginButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Logging in...' : 'Log In'}
          </LoginButton>
          
          <ForgotPasswordLink to="/forgot-password">Forgot your password?</ForgotPasswordLink>
          
          <RegisterLink to="/register">Need an account? Register here</RegisterLink>
        </LoginForm>

      </LoginCard>
    </LoginContainer>
  );
};

// Styled components
const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: ${props => props.theme.spacing.lg};
`;

const LoginCard = styled.div`
  background-color: ${props => props.theme.colors.verizonWhite};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.large};
  width: 100%;
  max-width: 450px;
  padding: ${props => props.theme.spacing.xl};
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: ${props => props.theme.fontSizes.medium};
  transition: ${props => props.theme.transitions.short};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.opplaneBlue};
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.background};
    cursor: not-allowed;
  }
`;

const LoginButton = styled.button`
  background-color: ${props => props.theme.colors.opplaneBlue};
  color: ${props => props.theme.colors.verizonWhite};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.medium};
  font-weight: bold;
  cursor: pointer;
  transition: ${props => props.theme.transitions.short};
  margin-top: ${props => props.theme.spacing.md};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.opplaneDarkBlue};
  }
  
  &:disabled {
    background-color: rgba(0, 102, 204, 0.5);
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  background-color: rgba(0, 102, 204, 0.1);
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const OpplaneLogo = styled.img`
  height: 20px;
`;

const ForgotPasswordLink = styled(Link)`
  color: ${props => props.theme.colors.lightText};
  text-align: center;
  margin-top: ${props => props.theme.spacing.md};
  text-decoration: none;
  display: block;
  font-size: ${props => props.theme.fontSizes.small};
  
  &:hover {
    color: ${props => props.theme.colors.opplaneBlue};
    text-decoration: underline;
  }
`;

const RegisterLink = styled(Link)`
  color: ${props => props.theme.colors.opplaneBlue};
  text-align: center;
  margin-top: ${props => props.theme.spacing.md};
  text-decoration: none;
  display: block;
  
  &:hover {
    text-decoration: underline;
  }
`;

export default Login;
